import { Box, Flex, Text } from '@chakra-ui/react';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Modal, useToast } from '@morressier/alembic';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteEmailTemplateById } from 'src/api/email-service';

interface Props {
  templateId: string;
  organizationId: string;
  isDefaultTemplate?: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onDeleteSuccess?: () => void;
}

const DeleteTemplateModal = ({
  templateId,
  organizationId,
  isDefaultTemplate = false,
  onClose,
  onSuccess,
  onDeleteSuccess
}: Props) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const deleteTemplateMutation = useMutation({
    mutationFn: () => deleteEmailTemplateById(templateId, organizationId),
    onSuccess: () => {
      toast({
        variant: 'success',
        title: 'Template Deleted',
        duration: 3000,
        isClosable: true
      });

      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      queryClient.invalidateQueries({ queryKey: ['email-template-details'] });

      onSuccess?.();

      if (onDeleteSuccess) {
        onDeleteSuccess();
        return;
      }

      onClose();
    },
    onError: error => {
      toast({
        variant: 'error',
        title: 'Failed to delete template',
        description:
          (error as { response?: { data?: { message?: string } } })?.response
            ?.data?.message || 'Something went wrong',
        duration: 5000,
        isClosable: true
      });

      onClose();
    }
  });

  const handleDeleteTemplate = () => {
    deleteTemplateMutation.mutate();
  };

  return (
    <Modal
      isOpen={!!templateId}
      onClose={onClose}
      width={{ base: 'calc(100vw - 16px)', md: '450px' } as any}
      closeButtonProps={{
        hidden: true
      }}
    >
      <Flex
        flexDirection="column"
        gap={4}
        padding={{ base: 4, md: 8 }}
        justifyContent="center"
        alignItems="center"
      >
        <Box fontSize="40px" color="actionSecondaryCautionContent">
          <FontAwesomeIcon icon={faEnvelope} fontSize="42px" />
        </Box>
        <Text textStyle="headingGroup">
          {isDefaultTemplate
            ? 'Default Template Cannot Be Deleted'
            : 'Delete Template from Workspace?'}
        </Text>
        <Text textStyle="textBody" textAlign="center">
          {isDefaultTemplate
            ? 'To delete this template, please make another template default first. After that, you will be able to delete it.'
            : 'This template will be permanently removed from your workspace. Are you sure you want to delete this template?'}
        </Text>
        <Flex flexDirection="column" gap={2} width="100%">
          {isDefaultTemplate ? (
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          ) : (
            <>
              <Button
                variant="secondary"
                onClick={onClose}
                isDisabled={deleteTemplateMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="caution"
                onClick={handleDeleteTemplate}
                isDisabled={deleteTemplateMutation.isPending}
                isLoading={deleteTemplateMutation.isPending}
              >
                Delete Template
              </Button>
            </>
          )}
        </Flex>
      </Flex>
    </Modal>
  );
};

export default DeleteTemplateModal;
